import { Entitlements } from '../../../entitlements';
import { NavigationItem } from '../../../navigation';
import { Feature } from '../../feature-flags';
import {
    DeviceType,
    ProductType,
    PwaVisibilityFeature,
    PwaVisibilityFeatureConditions,
    WidgetType,
} from './feature-visibility.pwa.types';

/**
 *  To be visible, each feature condition property must have at least
 *  one of the provided values returned as true for the FI or user.
 */
export const PwaVisibilityFeatureConditionMap = new Map<
    PwaVisibilityFeature,
    PwaVisibilityFeatureConditions
>([
    [
        PwaVisibilityFeature.Accounts,
        {
            navigationKeys: NavigationItem.Accounts,
        },
    ],
    [
        PwaVisibilityFeature.AccountsWidget,
        {
            widgetType: WidgetType.Accounts,
        },
    ],
    [
        PwaVisibilityFeature.ACH,
        {
            navigationKeys: NavigationItem.Ach,
            productTypes: ProductType.ACH,
            entitlements: [
                Entitlements.RestrictAchBatch,
                Entitlements.WorkAchExceptions,
                Entitlements.CreateAchTemplate,
                Entitlements.FullEditAchTemplate,
                Entitlements.PartialEditAchTemplate,
                Entitlements.DeleteAchTemplate,
                Entitlements.ApproveAchTemplate,
                Entitlements.CreateAchPayment,
                Entitlements.FullEditAchPayment,
                Entitlements.PartialEditAchPayment,
                Entitlements.DeleteAchPayment,
                Entitlements.ApproveAchPayment,
                Entitlements.CreateInternationalAchPayment,
                Entitlements.FullEditInternationalAchPayment,
                Entitlements.PartialEditInternationalAchPayment,
                Entitlements.DeleteInternationalAchPayment,
                Entitlements.ApproveInternationalAchPayment,
                Entitlements.FileUploadEditInternationalAchPayment,
            ],
        },
    ],
    [
        PwaVisibilityFeature.AchFileActivity,
        {
            navigationKeys: NavigationItem.Ach,
            featureFlags: Feature.AchFileActivity,
            deviceType: DeviceType.DesktopOnly,
        },
    ],
    [
        PwaVisibilityFeature.AchNoticeOfChange,
        {
            navigationKeys: NavigationItem.Ach,
            featureFlags: Feature.AchNoticeOfChange,
            deviceType: DeviceType.DesktopOnly,
        },
    ],
    [
        PwaVisibilityFeature.AchMasterRecipients,
        {
            navigationKeys: NavigationItem.Ach,
            featureFlags: Feature.PwaAchMasterRecipients,
            deviceType: DeviceType.DesktopOnly,
        },
    ],
    [
        PwaVisibilityFeature.AchRecipientActivity,
        {
            navigationKeys: NavigationItem.AchRecipientActivity,
            featureFlags: Feature.PwaAchRecipientActivity,
            deviceType: DeviceType.DesktopOnly,
        },
    ],
    [
        PwaVisibilityFeature.AchReturnActivity,
        {
            navigationKeys: NavigationItem.AchReturnActivity,
            featureFlags: Feature.PwaAchReturnActivity,
            deviceType: DeviceType.DesktopOnly,
        },
    ],
    [
        PwaVisibilityFeature.AchChildSupport,
        {
            navigationKeys: NavigationItem.Ach,
            featureFlags: Feature.PwaAchChildSupport,
        },
    ],
    [
        PwaVisibilityFeature.AchImportLayout,
        {
            navigationKeys: NavigationItem.AchImportLayout,
            featureFlags: Feature.PwaAchImportLayout,
            deviceType: DeviceType.DesktopOnly,
        },
    ],
    [
        PwaVisibilityFeature.AchPaymentTypeSelector,
        {
            navigationKeys: NavigationItem.Ach,
            featureFlags: Feature.PwaAchPaymentTypeSelector,
        },
    ],
    [
        PwaVisibilityFeature.AchInternationalTemplates,
        {
            navigationKeys: NavigationItem.Ach,
            featureFlags: Feature.PwaAchInternationalTemplates,
            entitlements: [
                Entitlements.CreateInternationalAchPayment,
                Entitlements.FullEditInternationalAchPayment,
                Entitlements.PartialEditInternationalAchPayment,
                Entitlements.DeleteInternationalAchPayment,
                Entitlements.ApproveInternationalAchPayment,
            ],
            deviceType: DeviceType.DesktopOnly,
        },
    ],
    [
        PwaVisibilityFeature.AchTemplatesList,
        {
            navigationKeys: NavigationItem.Ach,
            featureFlags: Feature.PwaAchTemplatesList,
        },
    ],
    [
        PwaVisibilityFeature.AchTaxPayments,
        {
            navigationKeys: NavigationItem.Ach,
            featureFlags: Feature.PwaAchTaxPayments,
        },
    ],
    [
        PwaVisibilityFeature.AgileticsWebEscrow,
        {
            navigationKeys: NavigationItem.Accounts,
            featureFlags: Feature.PwaAgileticsWebEscrow,
        },
    ],
    [
        PwaVisibilityFeature.CentrixSso,
        {
            navigationKeys: NavigationItem.PosPay,
            productTypes: ProductType.PositivePay,
            featureFlags: Feature.CentrixSso,
        },
    ],
    [
        PwaVisibilityFeature.Admin,
        {
            isAdmin: true,
            navigationKeys: NavigationItem.Admin,
            featureFlags: Feature.PwaAdminSection,
            deviceType: DeviceType.DesktopOnly,
        },
    ],
    [
        PwaVisibilityFeature.BillPay,
        {
            navigationKeys: NavigationItem.BillPay,
            productTypes: ProductType.BillPay,
            entitlements: Entitlements.BusinessBillPay,
        },
    ],
    [
        PwaVisibilityFeature.Conversations,
        {
            featureFlags: Feature.CoBrowse,
            uisEnabled: true,
        },
    ],
    [
        PwaVisibilityFeature.InfoCenterWidget,
        {
            widgetType: WidgetType.InfoCenter,
        },
    ],
    [
        PwaVisibilityFeature.Loans,
        {
            navigationKeys: NavigationItem.Loans,
            productTypes: ProductType.Transfers,
        },
    ],
    [
        PwaVisibilityFeature.LoanWidget,
        {
            productTypes: ProductType.Transfers,
            widgetType: WidgetType.QuickLoanPayment,
            entitlements: Entitlements.CreateTransfer,
        },
    ],
    [
        PwaVisibilityFeature.PaymentApprovalWidget,
        {
            widgetType: WidgetType.PaymentApproval,
        },
    ],
    [
        PwaVisibilityFeature.PendingApprovalsUsers,
        {
            isAdmin: true,
        },
    ],
    [
        PwaVisibilityFeature.PendingApprovalsAch,
        {
            entitlements: [
                Entitlements.ApproveAchPayment,
                Entitlements.ApproveInternationalAchPayment,
            ],
        },
    ],
    [
        PwaVisibilityFeature.PendingApprovalsTransfers,
        {
            entitlements: Entitlements.ApproveTransfer,
        },
    ],
    [
        PwaVisibilityFeature.PendingApprovalsWires,
        {
            entitlements: [
                Entitlements.ApproveInternationalWirePayment,
                Entitlements.ApproveDomesticWirePayment,
            ],
        },
    ],
    [
        PwaVisibilityFeature.PendingApprovalsWidget,
        {
            widgetType: WidgetType.QuickTransfer,
        },
    ],
    [
        PwaVisibilityFeature.PosPayWidget,
        {
            navigationKeys: NavigationItem.PosPay,
            productTypes: ProductType.PositivePay,
            widgetType: WidgetType.PosPay,
        },
    ],
    [
        PwaVisibilityFeature.PosPayAchExceptions,
        {
            productTypes: ProductType.PositivePay,
            navigationKeys: NavigationItem.PosPayAchExceptions,
            entitlements: Entitlements.WorkAchExceptions,
        },
    ],
    [
        PwaVisibilityFeature.PosPayCheckExceptions,
        {
            productTypes: ProductType.PositivePay,
            navigationKeys: NavigationItem.PosPayCheckExceptions,
            entitlements: Entitlements.WorkCheckExceptions,
        },
    ],
    [
        PwaVisibilityFeature.ArpIssuedItemsActivity,
        {
            productTypes: ProductType.PositivePay,
            navigationKeys: NavigationItem.ArpIssuedItemsActivity,
            featureFlags: Feature.ArpIssuedItemsActivity,
        },
    ],
    [
        PwaVisibilityFeature.ArpIssuedItemsFileActivity,
        {
            productTypes: ProductType.PositivePay,
            navigationKeys: NavigationItem.ArpIssuedItemsFileActivity,
            featureFlags: Feature.ArpIssuedItemsFileActivity,
        },
    ],
    [
        PwaVisibilityFeature.ArpIssuedItemsCreate,
        {
            productTypes: ProductType.PositivePay,
            featureFlags: Feature.ArpIssuedItemsCreate,
        },
    ],
    [
        PwaVisibilityFeature.RDC,
        {
            productTypes: ProductType.RDC,
            entitlements: Entitlements.RemoteDeposits,
            featureFlags: Feature.MobileDeposits,
            rdcUserConfigured: true,
        },
    ],
    [
        PwaVisibilityFeature.ReportsWidget,
        {
            widgetType: WidgetType.Reports,
            featureFlags: Feature.PwaReportsMenuItem,
        },
    ],
    [
        PwaVisibilityFeature.Reports,
        {
            navigationKeys: NavigationItem.Reports,
            productTypes: ProductType.Reporting,
            featureFlags: Feature.PwaReportsMenuItem,
        },
    ],
    [
        PwaVisibilityFeature.AccountReconReporting,
        {
            navigationKeys: NavigationItem.AccountReconciliation,
            featureFlags: Feature.AccountReconReporting,
        },
    ],
    [
        PwaVisibilityFeature.ElectronicDocuments,
        {
            navigationKeys: NavigationItem.EStatements,
            featureFlags: Feature.PwaElectronicDocuments,
        },
    ],
    [
        PwaVisibilityFeature.WausauEStatements,
        {
            navigationKeys: NavigationItem.EStatements,
            featureFlags: Feature.WausauStatementsEnabled,
        },
    ],
    [
        PwaVisibilityFeature.BdiEStatements,
        {
            navigationKeys: NavigationItem.BdiEStatements,
            featureFlags: Feature.BdiEStatementsEnabled,
        },
    ],
    [
        PwaVisibilityFeature.ResourcesWidget,
        {
            widgetType: WidgetType.Resources,
        },
    ],
    [
        PwaVisibilityFeature.CheckLookupWidget,
        {
            widgetType: WidgetType.CheckLookup,
            featureFlags: Feature.CheckLookupWidget,
            deviceType: DeviceType.DesktopOnly,
        },
    ],
    [
        PwaVisibilityFeature.StopPaymentWidget,
        {
            widgetType: WidgetType.StopPayment,
        },
    ],
    [
        PwaVisibilityFeature.Transfers,
        {
            navigationKeys: NavigationItem.Transfers,
            productTypes: ProductType.Transfers,
            entitlements: [
                Entitlements.CreateTransfer,
                Entitlements.EditTransfer,
                Entitlements.DeleteTransfer,
                Entitlements.ApproveTransfer,
            ],
        },
    ],
    [
        PwaVisibilityFeature.TransferWidget,
        {
            widgetType: WidgetType.QuickTransfer,
            featureFlags: Feature.PwaTransferPaymentWidget,
            productTypes: ProductType.Transfers,
            entitlements: [Entitlements.CreateTransfer],
            deviceType: DeviceType.DesktopOnly,
        },
    ],
    [
        PwaVisibilityFeature.Wires,
        {
            navigationKeys: NavigationItem.Wires,
            productTypes: [ProductType.Wires, ProductType.WiresFX],
            entitlements: [
                Entitlements.CreateDomesticWire,
                Entitlements.CreateInternationalWire,
                Entitlements.EditDomesticWirePayment,
                Entitlements.EditInternationalWirePayment,
                Entitlements.DeleteDomesticWirePayment,
                Entitlements.DeleteInternationalWirePayment,
                Entitlements.ApproveInternationalWirePayment,
                Entitlements.RejectWire,
                Entitlements.WireEditBeneficiary,
                Entitlements.WireApproveBeneficiary,
            ],
        },
    ],
    [
        PwaVisibilityFeature.WiresMultiple,
        {
            featureFlags: Feature.PwaWireMultipleWorkflow,
            navigationKeys: NavigationItem.Wires,
            productTypes: ProductType.Wires,
            entitlements: [Entitlements.CreateDomesticWire],
            deviceType: DeviceType.DesktopOnly,
        },
    ],
    [
        PwaVisibilityFeature.WiresUpload,
        {
            featureFlags: Feature.PwaWireFileUpload,
            navigationKeys: NavigationItem.Wires,
            productTypes: ProductType.Wires,
            entitlements: [Entitlements.WireUpload],
            deviceType: DeviceType.DesktopOnly,
        },
    ],
    [
        PwaVisibilityFeature.WiresFX,
        {
            featureFlags: Feature.PwaWireFx,
            navigationKeys: NavigationItem.WiresFX,
            productTypes: ProductType.WiresFX,
            entitlements: [Entitlements.CreateFxWire],
            deviceType: DeviceType.DesktopOnly,
        },
    ],
    [
        PwaVisibilityFeature.JhWires,
        {
            featureFlags: Feature.UseJhWires,
            graphFeatures: ['wires'],
        },
    ],
    [
        PwaVisibilityFeature.CorpaySso,
        {
            featureFlags: [Feature.PwaCorpaySso, Feature.SsoNavigations],
            navigationKeys: NavigationItem.WiresFX,
            productTypes: ProductType.WiresFX,
            deviceType: DeviceType.DesktopOnly,
        },
    ],
    [
        PwaVisibilityFeature.StopPayments,
        {
            navigationKeys: NavigationItem.StopPayment,
            productTypes: ProductType.StopPayment,
            featureFlags: Feature.PwaStopPayments,
        },
    ],
    [
        PwaVisibilityFeature.DashboardFeatureSearch,
        {
            featureFlags: Feature.PwaDashboardFeatureSearch,
        },
    ],
]);
